import { inject, Injectable, resource, computed } from "@angular/core";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { IJobsCurrentSettings, IJobsCurrentSettingsResponse } from "../interfaces/jobs-current-settings-response.interface";
import { AuthService } from "../auth";
import { IJobsJobAccess, JobsJobAccessField } from "@app/shared/interfaces/jobs-job-access.interface";
import { JobsBillingCard } from "@app/views-interfaces";
import type { JobsAndBillingResourceParams } from "@shared/interfaces";
import { ApplicationType } from "@app/views-interfaces";
import { DashboardsService } from "@shared/dashboards-service";

@Injectable({ providedIn: "root" })
export class JobsAndBillingCommonService {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);

    private currentSettingsLoadPromise: Promise<IJobsCurrentSettings | undefined> | null = null;

    private readonly rolesAccess = {
        can_jobs_view_add_edit: {
            job_status: true,
            view_job: () => true,
            job_notes: true,
            job_priority: true,
            currently_responsible: true,
            job_partner: true,
            job_manager: true,
            job_owner: true,
            target_start_date: true,
            target_end_date: true,
            job_group_1: true,
            job_group_2: true,
            drag_drop: true,
        } as IJobsJobAccess,
        can_workflow_resource: {
            job_status: false,
            view_job: () => true,
            job_notes: true,
            job_priority: true,
            currently_responsible: true,
            job_partner: true,
            job_manager: true,
            job_owner: true,
            target_start_date: true,
            target_end_date: true,
            job_group_1: true,
            job_group_2: true,
            drag_drop: false,
        } as IJobsJobAccess,
        can_jobs_view_all: {
            job_status: true,
            view_job: () => true,
            job_notes: true,
            job_priority: false,
            currently_responsible: false,
            job_partner: false,
            job_manager: false,
            job_owner: false,
            target_start_date: false,
            target_end_date: false,
            job_group_1: false,
            job_group_2: false,
            drag_drop: true,
        } as IJobsJobAccess,
        can_jobs_view_my: {
            job_status: true,
            view_job: (card: JobsBillingCard) => this.hasMyAccess(card),
            job_notes: true,
            job_priority: false,
            currently_responsible: false,
            job_partner: false,
            job_manager: false,
            job_owner: false,
            target_start_date: false,
            target_end_date: false,
            job_group_1: false,
            job_group_2: false,
            drag_drop: true,
        } as IJobsJobAccess,
        can_jobs_view_edit_all_job_resources: {
            job_status: true,
            view_job: () => true,
            job_notes: true,
            job_priority: false,
            currently_responsible: true,
            job_partner: false,
            job_manager: false,
            job_owner: false,
            target_start_date: false,
            target_end_date: false,
            job_group_1: false,
            job_group_2: false,
            drag_drop: true,
        } as IJobsJobAccess,
        can_jobs_view_edit_my_job_resources: {
            job_status: true,
            view_job: (card: JobsBillingCard) => this.hasMyAccess(card),
            job_notes: true,
            job_priority: false,
            currently_responsible: true,
            job_partner: false,
            job_manager: false,
            job_owner: false,
            target_start_date: false,
            target_end_date: false,
            job_group_1: false,
            job_group_2: false,
            drag_drop: true,
        } as IJobsJobAccess,
    };

    private currentSettingsResource = resource<IJobsCurrentSettings | undefined, JobsAndBillingResourceParams>({
        params: () => ({
            currentUser: this.authService._currentUser(),
            currentDashboard: this.dashboardsService.currentDashboard() ?? null,
        }),
        loader: ({ params }: { params: JobsAndBillingResourceParams }) => {
            const { currentUser, currentDashboard } = params;
            if (!currentUser || !currentDashboard || currentDashboard.ApplicationType !== ApplicationType.JobsBilling) {
                return Promise.resolve(undefined);
            }
            return this.getCurrentSettings();
        },
    });

    public currentSettings = computed(() => this.currentSettingsResource.value());

    public async getCurrentSettings(): Promise<IJobsCurrentSettings | undefined> {
        if (!this.currentSettingsLoadPromise) this.currentSettingsLoadPromise = this.loadCurrentSettings();
        return this.currentSettingsLoadPromise;
    }

    private async loadCurrentSettings(): Promise<IJobsCurrentSettings | undefined> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getJobsAndBillingCurrentSettings", null, "gotCurrentSettings");
            if (result.status === "success") {
                const response = result.response as IJobsCurrentSettingsResponse;
                return response?.data?.settings;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    private hasMyAccess(card: JobsBillingCard): boolean {
        const userId = this.authService.currentUser?.ifirm_userid;
        if (!userId) {
            return false;
        }

        return (
            card.job.currentlyResponsible === userId ||
            card.job.resources?.allocated?.some((resource) => resource.userId === userId) === true ||
            card.job.resources?.nonAllocated?.some((resource) => resource.userId === userId) === true
        );
    }

    hasAccess(access: JobsJobAccessField, card: JobsBillingCard | null = null): boolean {
        for (const [roleName, permissions] of Object.entries(this.rolesAccess)) {
            if (this.authService.currentUser?.[roleName as keyof typeof this.authService.currentUser] === "True") {
                const permission = (permissions as Partial<IJobsJobAccess>)[access];
                const granted = typeof permission === "function" ? card !== null && permission(card) : permission;
                if (granted) {
                    return true;
                }
            }
        }
        return false;
    }

    async deleteCard(cardId: string): Promise<boolean> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("deleteJobsAndBillingCard", { cardId }, "jobsAndBillingCardDeleted");
            if (result.status === "success") {
                return true;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return false;
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return false;
        }
    }
}
